<script setup>
// Widgets can be duplicated either into the same dashboard or a different asset within the same asset/organizational scope. It is done from here.
import { cloneDeep } from 'lodash-es';
import { useBiStore } from '~/bi/store/bi.store';
import { useCommonImports } from '~/common/composables/common-imports.composable';
import useEmitter from '~/common/composables/useEmitter';
import { sleep } from '~/common/utils/common.utils';

const props = defineProps({
  widgetId: {
    type: String,
    required: true,
  },
  widgetConfig: {
    type: Object,
    required: true,
  },
  widgetName: {
    type: String,
    required: true,
  },
  widgetLayout: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close']);

const { $t, $toast } = useCommonImports();
const emitter = useEmitter();
const bi_store = useBiStore();

const form$ = ref(null);

const state = reactive({
  is_loading: false,
});

const dashboard_options = computed(() => {
  return bi_store.sorted_dashboards
    .map(d => ({ value: d.uid, label: d.name }));
});

function buildNewWidget() {
  return {
    uid: crypto.randomUUID(),
    name: props.widgetName,
    config: cloneDeep(props.widgetConfig),
    x: 0,
    y: 0,
    w: props.widgetLayout.w || 5,
    h: props.widgetLayout.h || 2,
    i: '1',
  };
}

async function duplicateToCurrentDashboard() {
  const uid = crypto.randomUUID();
  const current_grid_layout = bi_store.current_grid_layout;
  const current_widget_position = current_grid_layout.find(widget => widget.uid === props.widgetId);
  const new_x = current_widget_position.x;
  const new_y = current_widget_position.y + current_widget_position.h;

  bi_store.createWidget(
    uid,
    {
      config: cloneDeep(props.widgetConfig),
      name: `${props.widgetName} - ${$t('Copy')}`,
    },
    { x: new_x, y: new_y, w: current_widget_position.w, h: current_widget_position.h },
  );

  emit('close');

  await sleep(500);
  const el = document.querySelectorAll(`[uid='${uid}']`)?.[0];
  if (el) {
    el.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest',
    });
  }
}

async function onSave() {
  state.is_loading = true;
  try {
    const form_data = form$.value.data;
    const selected_value = form_data.dashboard;
    const is_current_dashboard = selected_value === bi_store.selected_dashboard;

    if (is_current_dashboard) {
      await duplicateToCurrentDashboard();
      return;
    }

    const is_new = !bi_store.dashboards[selected_value];
    const new_widget = buildNewWidget();

    let target_dashboard_uid;
    let target_dashboard_name;

    if (is_new) {
      const created = await bi_store.createDashboard({
        name: selected_value,
        report_data: [new_widget],
      }, false);

      if (created.length)
        target_dashboard_uid = created[0].uid;

      target_dashboard_name = selected_value;
    }
    else {
      target_dashboard_uid = selected_value;
      const target_dashboard = bi_store.dashboards[target_dashboard_uid];
      target_dashboard_name = target_dashboard.name;
      const existing_widgets = target_dashboard.widgets || [];

      const y = existing_widgets.length > 0
        ? Math.max(...existing_widgets.map(w => (w.y || 0) + (w.h || 2)))
        : 0;
      const existing_ids = existing_widgets.map((w) => {
        const parsed = Number.parseInt(w.i);
        return Number.isNaN(parsed) ? 0 : parsed;
      });
      const unique_i = String(existing_ids.length ? Math.max(...existing_ids) + 1 : 1);

      new_widget.y = y;
      new_widget.i = unique_i;

      await bi_store.updateDashboard(target_dashboard_uid, {
        report_data: [...existing_widgets, new_widget],
      });
    }

    $toast({
      title: $t('Widget duplicated successfully'),
      text: `${$t('Widget has been duplicated to')} "${target_dashboard_name}"`,
      type: 'success',
      action_button_text: $t('Go to dashboard'),
      timeout: 5000,
      listeners: {
        click: () => {
          emitter.emit('bi:navigate-to-dashboard', target_dashboard_uid);
        },
      },
    });

    emit('close');
  }
  catch (err) {
    logger.error('Failed to duplicate widget:', err);
    $toast({
      title: $t('Failed to duplicate widget'),
      text: $t('Please try again'),
      type: 'error',
    });
  }
  finally {
    state.is_loading = false;
  }
}
</script>

<template>
  <HawkModalContainer id="bi-duplicate-widget-modal">
    <Vueform
      ref="form$"
      size="sm"
      class="max-w-[600px]"
      :columns="{
        default: { container: 12, label: 4, wrapper: 12 },
        sm: { container: 12, label: 4, wrapper: 12 },
        md: { container: 12, label: 4, wrapper: 12 },
      }"
      :endpoint="onSave"
      :display-errors="false"
    >
      <div class="col-span-12">
        <HawkModalHeader @close="emit('close')">
          <template #title>
            {{ $t('Duplicate widget') }}
          </template>
        </HawkModalHeader>
        <HawkModalContent>
          <SelectElement
            name="dashboard"
            :label="$t('Duplicate to')"
            :description="$t('Choose an existing dashboard or create a new one')"
            :items="dashboard_options"
            :default="bi_store.selected_dashboard"
            :native="false"
            :search="true"
            :create="true"
            rules="required"
            append-to="#bi-duplicate-widget-modal"
          />
        </HawkModalContent>
        <HawkModalFooter>
          <template #right>
            <div class="flex justify-end w-full col-span-full">
              <HawkButton
                class="mr-4"
                type="outlined"
                @click="emit('close')"
              >
                {{ $t('Cancel') }}
              </HawkButton>
              <ButtonElement
                name="save"
                :loading="state.is_loading"
                submits
              >
                {{ $t('Duplicate') }}
              </ButtonElement>
            </div>
          </template>
        </HawkModalFooter>
      </div>
    </Vueform>
  </HawkModalContainer>
</template>
